/**
 * alertshub#7 - an Alerts Hub card opens the EHR at `/?view=<tab>&record=<id>&alert=<id>` (the hub's
 * contract §9b). With `contact=<id>` too it's a contact deep-link (contactUrl.ts, the client's
 * drawer); without one, `view` names a top-level tab: Tasks, with `record` the task to open.
 * One-shot: read on load, then dropped from the URL so a refresh doesn't reopen it.
 */

/** The EHR's own top-level tabs a link may name (Schedule, Messages and the rest open elsewhere). */
export const IN_APP_VIEWS = [
  'dashboard',
  'contacts',
  'notes',
  'forms_hub',
  'tasks',
  'settings',
  // #501 follow-up: "Reconnect your account" opens Data & settings → Email & calendar
  'email_settings',
];

export interface ViewLink {
  view: string;
  record?: string;
}

/** The top-level tab (and record) the current URL asks for, when it isn't a contact link. */
export function readViewParam(search: string = window.location.search): ViewLink | null {
  const p = new URLSearchParams(search);
  if (p.get('contact')) return null; // the contact drawer handles its own view / record
  const view = p.get('view');
  if (!view || !IN_APP_VIEWS.includes(view)) return null;
  const record = p.get('record');
  return { view, ...(record ? { record } : {}) };
}

/** alertshub#13: Form Builder is its own app; a hub card about a builder form opens it there. */
export const FORM_BUILDER_URL = 'https://forms.credifyfast.com/app';

/** Where a link to a view that lives in another app goes (?view=form_builder), else null. */
export function externalViewHref(search: string = window.location.search): string | null {
  const p = new URLSearchParams(search);
  if (p.get('contact')) return null;
  return p.get('view') === 'form_builder' ? FORM_BUILDER_URL : null;
}

/** Drop a consumed view link from the URL (no history entry). */
export function clearViewParam(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get('contact')) return;
  let changed = false;
  for (const k of ['view', 'record', 'alert']) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
  }
  if (changed)
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}
